// =============================================================================
// components/home/SignalsRow.tsx — the "Signals" FOMO strip on Home.
//
// A SMALL crypto/v2 teaser strip: token + mini sparkline + a 24h-move pill + a
// "v2" lock chip. NON-USABLE BY DESIGN — every card teases the CRYB v2 signals
// engine; tapping shows the Coming Soon gate (comingSoon()).
//
// LIVE DATA (P1-HOME): the row is now wired to real market data via
// services/markets.fetchMarketTokens() — no hardcoded demo series. Each card's
// sparkline is markets.approxSeries() over the token's REAL % changes, and the
// pill shows the token's REAL 24h move. When live data is unavailable the strip
// renders nothing (never a fake shape, never a spinner that lingers). The v2
// lock + "Coming in v2" copy stay: the DATA is live, the SIGNALS PRODUCT is not.
//
// All styling references tokens (constants/theme) — no ad-hoc hex/spacing.
// =============================================================================
import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '../ui/Icon';
import { colors, spacing, radius, fonts, typographyScale } from '../../constants/theme';
import { GlassCard } from '../glass';
import { comingSoon } from '../../lib/comingSoon';
import {
  fetchMarketTokens,
  approxSeries,
  type MarketToken,
} from '../../services/markets';
import Sparkline from './Sparkline';

// Per-symbol accent tint for the token badge. Falls back to the brand purple so
// an unmapped coin still reads on-brand (no ad-hoc hex at the call site).
const TINTS: Record<string, string> = {
  BTC: colors.btcOrange,
  ETH: colors.ethBlue,
  SOL: '#9945FF',
  BNB: '#F0B90B',
  XRP: '#23292F',
  ADA: '#0033AD',
  DOGE: '#C2A633',
  LINK: '#2A5ADA',
  AVAX: '#E84142',
  MATIC: '#8247E5',
  POL: '#8247E5',
  DOT: '#E6007A',
};
function tintFor(symbol: string): string {
  return TINTS[symbol?.toUpperCase()] ?? colors.primary;
}

// Only the majors make sense as a teaser strip — cap the count so the row stays
// SMALL (design bar: a slim FOMO strip, not a second markets screen).
const MAX_SIGNALS = 6;

function fmtChange(pct?: number | null): string {
  if (pct == null || !isFinite(pct)) return '—';
  const s = pct >= 0 ? '+' : '';
  return `${s}${pct.toFixed(2)}%`;
}

function SignalCard({ token }: { token: MarketToken }) {
  const up = (token.change24h ?? 0) >= 0;
  const dirColor = up ? colors.success : colors.error;
  const tint = tintFor(token.symbol);
  const series = approxSeries(token, 16);

  return (
    <GlassCard
      padding={spacing.sm}
      radius={radius.md}
      onPress={() => comingSoon('Signals')}
      style={styles.card}
      accessibilityLabel={`${token.name} signal preview. ${fmtChange(token.change24h)} in 24 hours. Signals product coming in CRYB v2.`}
    >
      {/* Token identity + v2 lock chip (single line — no subline) */}
      <View style={styles.cardTop}>
        <View style={[styles.tokenBadge, { backgroundColor: tint + '22' }]}>
          <Text style={[styles.tokenSymbol, { color: tint }]}>{token.symbol}</Text>
        </View>
        <Text style={styles.tokenName} numberOfLines={1}>{token.name}</Text>
        <View style={styles.lockChip}>
          <Ionicons name="lock-closed" size={9} color={colors.primary} />
          <Text style={styles.lockChipText}>v2</Text>
        </View>
      </View>

      {/* Slim live sparkline built from the token's REAL % changes */}
      <View style={styles.sparkWrap}>
        <Sparkline data={series.length >= 2 ? series : [0, 0]} width={96} height={22} color={dirColor} />
        <View style={[styles.chancePill, { backgroundColor: dirColor + '18' }]}>
          <Ionicons name={up ? 'trending-up' : 'trending-down'} size={11} color={dirColor} />
          <Text style={[styles.chanceText, { color: dirColor }]}>{fmtChange(token.change24h)}</Text>
        </View>
      </View>
    </GlassCard>
  );
}

// Module-level cache of the last good fetch. The home feed can remount this row
// (header re-create / pull-to-refresh / socket-driven re-render); without a cache
// each remount replayed loading→null→data and the strip visibly flickered on/off
// (#74). Seeding from the cache renders the last data instantly, no flash.
let _signalsCache: MarketToken[] | null = null;

export default function SignalsRow() {
  const [tokens, setTokens] = useState<MarketToken[]>(_signalsCache ?? []);
  const [loading, setLoading] = useState(_signalsCache === null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const { tokens: list } = await fetchMarketTokens();
        if (!mounted.current) return;
        const next = Array.isArray(list) ? list.slice(0, MAX_SIGNALS) : [];
        // Only overwrite a good cache with a non-empty result — a transient empty
        // (rate-limit/offline) must not blank an already-showing strip.
        if (next.length > 0) {
          _signalsCache = next;
          setTokens(next);
        } else if (_signalsCache === null) {
          setTokens([]);
        }
      } catch {
        // Keep whatever we already have; never blank on a transient error.
        if (mounted.current && _signalsCache === null) setTokens([]);
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => { mounted.current = false; };
  }, []);

  // No live data (offline / rate-limited / still loading) → render nothing.
  // Honest by design: no fabricated shapes, no lingering spinner in the header.
  if (loading || tokens.length === 0) return null;

  return (
    <View style={styles.section}>
      {/* Section header: title + LIVE badge + coming-in-v2 chip */}
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <Ionicons name="pulse" size={16} color={colors.primary} />
          <Text style={styles.headerTitle}>Signals</Text>
          <View style={styles.liveBadge}>
            <View style={styles.liveDot} />
            <Text style={styles.liveBadgeText}>LIVE</Text>
          </View>
        </View>
        <TouchableOpacity
          onPress={() => comingSoon('Signals')}
          accessibilityRole="button"
          accessibilityLabel="Signals product coming in CRYB v2"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Text style={styles.comingText}>Coming in v2</Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}
      >
        {tokens.map((t) => (
          <SignalCard key={t.id || t.symbol} token={t} />
        ))}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    marginTop: spacing.xs,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2,
  },
  headerTitle: {
    fontSize: 13,
    color: colors.ink,
    ...fonts.semibold,
  },
  liveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.success + '18',
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.success,
  },
  liveBadgeText: {
    fontSize: 9,
    letterSpacing: 0.8,
    color: colors.success,
    ...fonts.bold,
  },
  comingText: {
    ...typographyScale.caption,
    color: colors.primary,
    ...fonts.semibold,
  },
  row: {
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
  },
  card: {
    width: 132,
  },
  cardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2,
  },
  tokenBadge: {
    width: 24,
    height: 24,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tokenSymbol: {
    fontSize: 9,
    ...fonts.extrabold,
  },
  tokenName: {
    flex: 1,
    fontSize: 12,
    color: colors.ink,
    ...fonts.semibold,
  },
  lockChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    backgroundColor: colors.primary + '14',
    borderRadius: radius.full,
    paddingHorizontal: spacing.xs + 2,
    paddingVertical: 2,
  },
  lockChipText: {
    fontSize: 9,
    color: colors.primary,
    ...fonts.bold,
  },
  sparkWrap: {
    marginTop: spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.xs,
  },
  chancePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    borderRadius: radius.full,
    paddingHorizontal: spacing.xs + 2,
    paddingVertical: 2,
  },
  chanceText: {
    fontSize: 10,
    ...fonts.bold,
  },
});
